import type { IconName } from "@/components/icons/Icon";
import {
  CATEGORY_NAV_SETTINGS_ICONS,
  CATEGORY_NAV_SETTINGS_ITEMS,
} from "@/components/settings/category-nav-settings.constants";
import {
  SETTINGS_NAV_ICONS,
  SETTINGS_SIDEBAR_ITEMS,
  SettingSidebarItem,
  SettingsNavbarId,
  SettingsSidebarItemGroup,
} from "@/components/settings/settings.constants";
import { listConversations } from "@/gen_src/proto/chalk/agent/v1/conversation-AgentConversationService_connectquery";
import { TeamCreationType } from "@/gen_src/proto/chalk/server/v1/team_pb";
import { isDefined } from "@/utils/function-utils";
import { useFlagsV2 } from "@/utils/hooks/use-flags-grpc";
import { useDANGEROUSisChalkEmployee } from "@/utils/hooks/use-is-chalk-employee";
import { useIsServerlessEnvironment } from "@/utils/hooks/use-is-serverless-environment";
import { useHasPermissionForEndpoint } from "@/utils/hooks/use-permissions";
import { useRouteAndTeamInfo } from "@/utils/hooks/url/use-route-info";
import { useCategoryNavEnabled } from "@/utils/hooks/use-category-nav-enabled";
import { useMemo } from "react";

export interface UseSettingsPanesProps {
  currentPaneId: string | null | undefined;
}

export interface UseSettingsPanesHook {
  paneGroups: SettingsSidebarItemGroup[];
  currentPane: SettingSidebarItem | null;
}

interface SettingsSidebarDefinitions {
  groups: readonly { title: string; panes: readonly SettingSidebarItem[] }[];
  icons: Partial<Record<SettingsNavbarId, IconName>>;
}

const CLASSIC_SETTINGS_DEFINITIONS: SettingsSidebarDefinitions = {
  groups: SETTINGS_SIDEBAR_ITEMS,
  icons: SETTINGS_NAV_ICONS,
};
const CATEGORY_NAV_SETTINGS_DEFINITIONS: SettingsSidebarDefinitions = {
  groups: CATEGORY_NAV_SETTINGS_ITEMS,
  icons: CATEGORY_NAV_SETTINGS_ICONS,
};

/** The Settings panes and icons of the active navigation: classic, or the category nav's. */
export const useSettingsSidebarDefinitions = (): SettingsSidebarDefinitions =>
  useCategoryNavEnabled() ? CATEGORY_NAV_SETTINGS_DEFINITIONS : CLASSIC_SETTINGS_DEFINITIONS;

export const useSettingPanes = (props: UseSettingsPanesProps): UseSettingsPanesHook => {
  const { currentPaneId } = props;
  const { environment, team } = useRouteAndTeamInfo();

  const isSelfSignupTeam = team?.teamCreationType === TeamCreationType.SELF_SERVICE;

  const coalescedPanelRelatedIds =
    environment?.clusterGatewayId ||
    environment?.clusterTimescaledbId ||
    environment?.clusterWorkflowOrchestratorId ||
    environment?.backgroundPersistenceDeploymentId;
  const { isServerless, loading: serverlessLoading } = useIsServerlessEnvironment();

  const flagsV2 = useFlagsV2();

  const canSeeAdminOnlyPanes = useDANGEROUSisChalkEmployee();
  const isChalkEmployee = useDANGEROUSisChalkEmployee({ ignoreAdminMode: true });
  // The assistant pane lists conversations, so it is only offered to users who may call that RPC.
  const { hasPermission: canReadAssistant } = useHasPermissionForEndpoint(listConversations);
  const { groups: settingsGroups } = useSettingsSidebarDefinitions();
  const paneGroups = useMemo((): SettingsSidebarItemGroup[] => {
    return settingsGroups.map((paneGroup) => ({
      ...paneGroup,
      panes: paneGroup.panes.filter((p) => {
        const pane: SettingSidebarItem = p;
        if (pane.shouldShowInternal && isChalkEmployee) {
          return true;
        }

        // Hide panes that are only for chalk employees if user is not a chalk employee
        if (pane.chalkEmployeeOnly && !canSeeAdminOnlyPanes) {
          return false;
        }

        // Hide serverless-gated panes while loading or from non-admins in serverless environments.
        if (
          pane.hideInServerless &&
          (serverlessLoading || (isServerless && !canSeeAdminOnlyPanes))
        ) {
          return false;
        }

        // Hide the resource usage dashboard while flags are loading.
        // If the flag `resource_usage_disabled` is set enabled, hide the resource usage dashboard.
        if (p.id === "usage-dashboard") {
          if (!flagsV2.ready) {
            return false;
          } else {
            return !flagsV2.enabled("resource_usage_disabled");
          }
        }

        if (p.id === "shared-resources") {
          return coalescedPanelRelatedIds != null || isChalkEmployee;
        }

        // Only offer the self-signup reset on a team that actually signed itself
        // up. Anywhere else the endpoint refuses the call, so the link would
        // lead nowhere but a disabled button — and on a real customer's team it
        // would read as an offer to delete them.
        if (p.id === "delete-team") {
          return isSelfSignupTeam;
        }

        if (p.id === "assistant") {
          return canReadAssistant;
        }

        if (p.id === "notebooks") {
          return flagsV2.enabled("notebooks_enabled") || canSeeAdminOnlyPanes;
        }

        return pane.flag != null ? flagsV2.enabled(pane.flag) : true;
      }),
    }));
  }, [
    canReadAssistant,
    canSeeAdminOnlyPanes,
    coalescedPanelRelatedIds,
    flagsV2,
    isChalkEmployee,
    isSelfSignupTeam,
    isServerless,
    serverlessLoading,
    settingsGroups,
  ]);

  const currentPane = useMemo((): SettingSidebarItem | null => {
    let defaultPane = paneGroups[0].panes[0];
    if (paneGroups.length > 1) {
      defaultPane = paneGroups[1].panes[0];
    }
    if (typeof window === "undefined") {
      return defaultPane;
    }

    const currentPane = currentPaneId || window.location.hash?.substring(1);
    if (!isDefined(currentPane)) {
      return defaultPane;
    }

    for (const paneGroup of paneGroups) {
      for (const pane of paneGroup.panes) {
        if (pane.id === currentPane) {
          return pane;
        }
      }
    }

    return defaultPane;
  }, [currentPaneId, paneGroups]);

  return {
    currentPane,
    paneGroups,
  };
};
